import React from 'react';
import Stack from '@mui/material/Stack';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { validateProps } from '../../../../../utils/propValidator';
import ImageViewer from '../../data-display/ImageViewer';
import { IMAGE_UPLOAD_BUTTON_TYPE } from './imageUpload.constants';
import { getImgUrl, normalizeImageValue, resolveMinImages } from './imageUpload.utils';
import { ImagePreviewButton, UploadImageButton } from './ImageUploadActions';
import ImageUploader from './ImageUploader';


interface ImageUploadProps {
  name?: string;
  rules?: Record<string, any>;
  showPreview?: boolean;
  previewAfter?: boolean;
  buttonText?: string | React.ReactNode;
  multiple?: boolean;
  aspectRatio?: number | string;
  uploaderTitle?: string;
  viewerTitle?: string;
  maxFileSize?: number;
  minDimensions?: { width: number; height: number };
  minImages?: number;
  maxImages?: number;
  buttonType?: string;
  sx?: any;
  id?: string;
  value?: any;
  onChange?: (...args: any[]) => void;
  onAfterChange?: () => any;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  [key: string]: any;
}

const propRules = {
  name: { type: 'string', required: true },
  rules: { type: 'object' },
  showPreview: { type: 'boolean' },
  previewAfter: { type: 'boolean' },
  buttonText: { type: ['string', 'node'] },
  multiple: { type: 'boolean' },
  aspectRatio: { type: ['number', 'string'] },
  uploaderTitle: { type: 'string' },
  maxFileSize: { type: 'number' },
  minDimensions: {
    type: 'object',
    shape: { width: { type: 'number' }, height: { type: 'number' } },
  },
  minImages: { type: 'number' },
  maxImages: { type: 'number' },
  buttonType: { oneOf: Object.values(IMAGE_UPLOAD_BUTTON_TYPE) },
  sx: { type: 'object' },
};

/**
 * ControlledImageUpload
 *
 * React Hook Form–controlled image upload field.
 * Requires usage inside FormProvider.
 *
 * @component
 *
 * @param {Object} props
 * @param {string} props.name
 * @param {Object} [props.rules]
 *   Note: If provided, rules.validate must be a function.
 * @param {boolean} [props.showPreview=true]
 * @param {boolean} [props.previewAfter=false]
 * @param {string|React.ReactNode} [props.buttonText]
 * @param {boolean} [props.multiple=false]
 * @param {number|string} [props.aspectRatio]
 * @param {string} [props.uploaderTitle]
 * @param {number} [props.maxFileSize]
 * @param {{width:number,height:number}} [props.minDimensions]
 * @param {number} [props.minImages]
 * @param {number} [props.maxImages]
 * @param {'default'|'icon'} [props.buttonType='default']
 * @param {Object} [props.sx]
 *
 * @ref {Object}
 * @ref {Function} ref.openUploader
 *
 * @returns {JSX.Element}
 */
const ControlledImageUpload = forwardRef<unknown, ImageUploadProps>((props, ref) => {
  validateProps(props as Record<string, unknown>, propRules, 'ControlledImageUpload');
  const { name = '', rules = {}, minImages, maxImages, ...rest } = props;

  const effectiveMinImages = resolveMinImages({ required: rules.required, minImages });

  const { control, trigger } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        ...rules,
        validate: (value) => {
          const images = Array.isArray(value) ? value : value ? [value] : [];

          if (typeof effectiveMinImages === 'number' && images.length < effectiveMinImages) {
            return `At least ${effectiveMinImages} image${
              effectiveMinImages > 1 ? 's' : ''
            } required`;
          }

          if (typeof maxImages === 'number' && images.length > maxImages) {
            return `Maximum ${maxImages} images allowed`;
          }

          if (typeof rules?.validate === 'function') {
            return rules.validate(value);
          }

          return true;
        },
      }}
      render={({ field }) => (
        <InputComponent
          {...field}
          ref={ref}
          required={!!rules?.required}
          minImages={effectiveMinImages}
          maxImages={maxImages}
          onAfterChange={() => trigger(name)}
          {...rest}
        />
      )}
    />
  );
});
ControlledImageUpload.displayName = 'ControlledImageUpload';

export default ControlledImageUpload;

/**
 * InputComponent
 *
 * Presentation-only image upload control.
 * Can be used with or without react-hook-form.
 *
 * @component
 *
 * @param {Object} props
 * @param {string} props.name
 * @param {Object|Object[]} [props.value]
 * @param {Function} props.onChange
 * @param {Function} [props.onAfterChange]
 * @param {boolean} [props.showPreview=true]
 * @param {boolean} [props.previewAfter=false]
 * @param {string|React.ReactNode} [props.buttonText]
 * @param {boolean} [props.multiple=false]
 * @param {number|string} [props.aspectRatio]
 * @param {string} [props.uploaderTitle]
 * @param {number} [props.maxFileSize]
 * @param {{width:number,height:number}} [props.minDimensions]
 * @param {boolean} [props.required=false]
 * @param {number} [props.minImages]
 * @param {number} [props.maxImages]
 * @param {'default'|'icon'} [props.buttonType='default']
 * @param {Object} [props.sx]
 *
 * @ref {Object}
 * @ref {Function} ref.openUploader
 *
 * @returns {JSX.Element}
 */
const InputComponent = forwardRef<unknown, ImageUploadProps>(
  (
    {
      value = [],
      onAfterChange,
      onChange,
      showPreview = true,
      previewAfter,
      buttonText,
      multiple = false,
      aspectRatio,
      uploaderTitle,
      maxFileSize,
      minDimensions,
      required,
      minImages,
      maxImages,
      buttonType = IMAGE_UPLOAD_BUTTON_TYPE.DEFAULT,
      sx,
    },
    ref,
  ) => {
    const [openUploader, setOpenUploader] = useState(false);
    const [openViewer, setOpenViewer] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        openUploader: () => setOpenUploader(true),
      }),
      [],
    );

    const images = useMemo(() => normalizeImageValue(value), [value]);

    const previewUrls = useMemo(() => images.map((img) => getImgUrl(img)), [images]);

    const handleSave = (items) => {
      const nextValue = multiple ? items : (items[0] ?? null);
      onChange(nextValue);
      onAfterChange?.();
      setOpenUploader(false);
    };

    const resolvedDirection = showPreview
      ? previewAfter && previewUrls.length
        ? 'row-reverse'
        : 'row'
      : 'row-reverse';

    return (
      <>
        <Stack direction={resolvedDirection} alignItems='center' gap={4} sx={sx}>
          <ImagePreviewButton
            showPreview={showPreview}
            multiple={multiple}
            previewLength={previewUrls.length}
            onClick={() => setOpenViewer(true)}
          />

          <UploadImageButton
            buttonType={buttonType}
            buttonText={buttonText}
            onClick={() => setOpenUploader(true)}
          />
        </Stack>

        <ImageUploader
          open={openUploader}
          onClose={() => setOpenUploader(false)}
          multiple={multiple}
          initialValue={images}
          aspectRatio={aspectRatio}
          maxSize={maxFileSize}
          minDimensions={minDimensions}
          required={required}
          title={uploaderTitle}
          onSave={handleSave}
          minImages={minImages}
          maxImages={maxImages}
        />

        <ImageViewer
          open={openViewer}
          onClose={() => setOpenViewer(false)}
          media={previewUrls}
          title={uploaderTitle}
        />
      </>
    );
  },
);
